import { Box, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextTerms.css';

export const RICH_TEXT_TERMS_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean']
  ]
};

export const RICH_TEXT_TERMS_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'link'
];

const decodeKnownEntities = (value: string) =>
  value
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");

export const richTextPlainText = (value: string) =>
  decodeKnownEntities(
    value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>|<\/div>|<\/li>|<\/h[1-6]>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
  )
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s+/g, '\n')
    .trim();

export const richTextHasContent = (value: string) =>
  richTextPlainText(value).length > 0;

export const isFormattedTerms = (value: string) =>
  /<\/?[a-z][\s\S]*>/i.test(value);

export const RichTextTermsEditor = ({
  value,
  onChange,
  label,
  helperText,
  placeholder = 'Write your terms and services content here…',
  maxLength
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  helperText?: string;
  placeholder?: string;
  maxLength?: number;
}) => {
  const accessibilityId = useRef(
    `rich-text-terms-${Math.random().toString(36).slice(2)}`
  ).current;
  const labelId = `${accessibilityId}-label`;
  const helpId = `${accessibilityId}-help`;
  const editorRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    const root = editorRef.current?.getEditor().root;
    if (!root) return;
    root.setAttribute('role', 'textbox');
    root.setAttribute('aria-multiline', 'true');
    root.setAttribute('aria-labelledby', labelId);
    root.setAttribute('aria-describedby', helpId);
  }, [helpId, labelId]);

  return (
    <Box className="profile-review-rich-text-editor">
      <Typography id={labelId} component="label" variant="subtitle2">
        {label}
      </Typography>
      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={RICH_TEXT_TERMS_MODULES}
        formats={RICH_TEXT_TERMS_FORMATS}
      />
      <Typography
        id={helpId}
        component="div"
        variant="caption"
        color={
          maxLength && value.length > maxLength ? 'error' : 'text.secondary'
        }
      >
        {helperText ||
          'Use the toolbar to format headings, lists, emphasis, and links.'}
        {maxLength
          ? ` ${value.length.toLocaleString()} / ${maxLength.toLocaleString()} characters.`
          : ''}
      </Typography>
    </Box>
  );
};

export const RichTextTermsContent = ({ value }: { value: string }) =>
  isFormattedTerms(value) ? (
    <Box
      className="profile-review-rich-text-content"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
    />
  ) : (
    <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>
      {value}
    </Typography>
  );
